import React from 'react';
import {
  Checkbox,
  FormControl,
  FormControlProps,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import makeStyles from '@mui/styles/makeStyles';
import { SelectOption } from '../../global';

interface MultiSelectProps extends Omit<SelectProps, 'onChange' | 'value'> {
  helperText?: string;
  dataTestId?: string;
  options: SelectOption[];
  value: (string | number)[];
  formControlProps?: FormControlProps;
  onChange: (selected: (string | number)[]) => void;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  error,
  options,
  onChange,
  helperText,
  dataTestId,
  value = [],
  formControlProps = {},
  ...props
}) => {
  const classes = useStyles();

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const selectedValues = event.target.value as string[];
    onChange(selectedValues);
  };

  const renderSelectedValues = (selected: (string | number)[]) => {
    const selectedLabels = options.filter(option => selected.includes(option.value)).map(option => option.label);
    return selectedLabels.join(', ');
  };

  return (
    <FormControl variant="outlined" size="small" fullWidth className={classes.formControl} {...formControlProps}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        label={label}
        error={!!error}
        onChange={handleChange}
        renderValue={selected => renderSelectedValues(selected as string[])}
        MenuProps={{
          style: { maxHeight: '500px' },
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        }}
        {...props}
        inputProps={{
          ...(props?.inputProps as any),
          ...(dataTestId ? { data_test_id: dataTestId } : {}),
        }}
      >
        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={value.indexOf(option.value) > -1} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
      {error && helperText && <FormHelperText error>{helperText}</FormHelperText>}
    </FormControl>
  );
};

const useStyles = makeStyles({
  formControl: {
    minWidth: 120,
  },
});
